// Executable-skills marketplace (S-EXEC P-E4, P3-1 UX — no gating yet).
//
// Mirrors SkillGalleryTab, but for the SCRIPTS tier: skills that carry code and
// run in the hardened gVisor sandbox. Browse + add-to-agent, exactly like the
// guidance gallery — the security lives at publish (review) and run (kill-switch
// + opt-in + approved-hash), NOT here, so the shelf stays open. Each card is
// honest about what it is: a "Code" tier chip + its review state, so a user
// knows a skill carries executable code and whether it's cleared to run.

import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { COLORS } from "../../theme/colors";
import { MATERIALS } from "../../theme/materials";
import { skillsApi } from "../../api/client";
import {
  formatLift, dataFitLabel, stars, installLabel, deriveCategories,
  reviewStateBadge, toneColor, isRunnable,
} from "./skillLibFormat";
import SkillDetailDrawer from "./SkillDetailDrawer";
import AttachToAgentModal from "./AttachToAgentModal";

const BORDER = COLORS.border || "#e2e5eb";
const MUTED = COLORS.textMuted || "#6b7280";
const DIM = COLORS.textDim || "#9090a8";
const GREEN = COLORS.green || "#10b981";
const ACCENT = COLORS.accent || "#5b5bf0";

function unwrapArray(res) {
  if (Array.isArray(res)) return res;
  if (res && Array.isArray(res.data)) return res.data;
  return [];
}

function CodeChip() {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10.5, fontWeight: 700,
      letterSpacing: "0.02em", padding: "2px 8px", borderRadius: 999,
      color: ACCENT, background: `${ACCENT}18`, border: `1px solid ${ACCENT}55`,
    }}>⚙ Code</span>
  );
}

function ReviewChip({ state }) {
  const badge = reviewStateBadge(state);
  if (!badge) return null;
  const c = toneColor(badge.tone, COLORS);
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10.5, fontWeight: 650,
      padding: "2px 8px", borderRadius: 999, color: c, background: `${c}18`,
      border: `1px solid ${c}55`,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: c }} />
      {badge.label}
    </span>
  );
}

function GalleryCard({ skill, onOpen, onAdd }) {
  const lift = formatLift(skill.eval_lift);
  const fit = dataFitLabel(skill.data_fit);
  const star = stars(skill.avg_rating);
  const installs = installLabel(skill.install_count);
  const runnable = isRunnable(skill);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(skill)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(skill); } }}
      style={{
        textAlign: "left", cursor: "pointer",
        border: `1px solid ${BORDER}`, borderRadius: 16, background: COLORS.surface,
        boxShadow: MATERIALS.spatialShadow(), padding: "14px 15px",
        display: "flex", flexDirection: "column", gap: 8,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 22 }}>{skill.icon || "⚙"}</span>
        <h3 style={{ fontSize: 13.5, margin: 0, color: COLORS.text, flex: 1, minWidth: 0 }}>
          {skill.display_name || skill.name}
        </h3>
        <CodeChip />
      </div>
      <div style={{ color: MUTED, fontSize: 12, lineHeight: 1.4, minHeight: 34 }}>{skill.description}</div>

      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: "auto" }}>
        <ReviewChip state={skill.review_state} />
        {lift && (
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 700,
            padding: "3px 9px", borderRadius: 999, color: GREEN,
            background: `${GREEN}22`, border: `1px solid ${GREEN}66`,
          }}>{lift}</span>
        )}
        {fit && (
          <span style={{ fontSize: 11.5, color: skill.data_fit === "works_with_data" ? GREEN : DIM }}>{fit}</span>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        {star && <span style={{ color: COLORS.orange || "#f59e0b", fontSize: 12 }}>{star}</span>}
        {installs && <span style={{ fontSize: 11, color: DIM }}>{installs}</span>}
        <span style={{ flex: 1 }} />
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onAdd(skill); }}
          title={runnable ? "Runs in the sandbox once added" : "Runs once it's reviewed and the sandbox is enabled"}
          style={{
            font: "inherit", fontSize: 11.5, fontWeight: 700, color: "#fff", background: ACCENT,
            border: "none", borderRadius: 999, padding: "5px 12px", cursor: "pointer",
          }}
        >＋ Add to an agent</button>
      </div>
    </div>
  );
}

export default function ExecutableSkillsGallery() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [category, setCategory] = useState("All");
  const [openSkill, setOpenSkill] = useState(null);
  const [attachSkill, setAttachSkill] = useState(null);

  const goAdopt = (agentId) => {
    setAttachSkill(null);
    setOpenSkill(null);
    navigate(`/library/${agentId}`);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = unwrapArray(await skillsApi.gallery());
        // The executable marketplace is the scripts tier only.
        if (!cancelled) setRows(data.filter((r) => r.tier === "scripts"));
      } catch {
        if (!cancelled) setError("Couldn't load the executable-skills marketplace.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const categories = useMemo(() => deriveCategories(rows), [rows]);
  const visible = useMemo(
    () => (category === "All" ? rows : rows.filter((r) => r.category === category)),
    [rows, category]
  );

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <h2 style={{ fontSize: 16, margin: 0, color: COLORS.text }}>Executable skills</h2>
        <p style={{ margin: "4px 0 0", color: DIM, fontSize: 13, maxWidth: "70ch" }}>
          Skills that carry <strong style={{ color: COLORS.text }}>code</strong> — they run in a hardened,
          single-use gVisor sandbox with no network and no credentials, reaching only your own data through a
          read-only broker. Add one to an agent just like any skill; it runs once it’s reviewed and the sandbox
          is switched on for your workspace.
        </p>
      </div>

      {/* Safety banner — set expectations up front */}
      <div style={{
        display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 14, padding: "11px 14px",
        borderRadius: 12, background: `${ACCENT}0f`, border: `1px solid ${ACCENT}55`,
        fontSize: 12.5, color: MUTED,
      }}>
        <span style={{ fontSize: 15 }}>🛡️</span>
        <div>
          <b style={{ color: COLORS.text }}>Safe by construction.</b> Every executable skill is reviewed by an
          administrator before it can run, runs isolated (no network, no secrets), and its output is fact-checked
          like any tool. A skill that hasn’t been reviewed yet still installs — it just won’t execute until it is.
        </div>
      </div>

      {/* Category chips */}
      {categories.length > 1 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
          {categories.map((c) => {
            const on = c === category;
            return (
              <button
                key={c}
                type="button"
                onClick={() => setCategory(c)}
                style={{
                  fontSize: 11.5, fontWeight: 600, cursor: "pointer",
                  padding: "6px 12px", borderRadius: 999,
                  color: on ? "#fff" : MUTED,
                  background: on ? ACCENT : COLORS.surface,
                  border: `1px solid ${on ? ACCENT : BORDER}`,
                }}
              >{c}</button>
            );
          })}
        </div>
      )}

      {loading ? (
        <div style={{ fontSize: 13, color: DIM }}>Loading executable skills…</div>
      ) : error ? (
        <div style={{ fontSize: 13, color: COLORS.red || "#ef4444" }}>{error}</div>
      ) : visible.length === 0 ? (
        <div style={{
          border: `1px dashed ${BORDER}`, borderRadius: 16, padding: "28px 20px", textAlign: "center",
          color: DIM,
        }}>
          <div style={{ fontSize: 26, marginBottom: 6 }}>⚙</div>
          <div style={{ fontSize: 13.5, color: COLORS.text, fontWeight: 600 }}>No executable skills yet</div>
          <div style={{ fontSize: 12.5, marginTop: 4, maxWidth: "48ch", marginInline: "auto" }}>
            When a skill that carries code is published to your workspace, it appears here. Import or author one,
            then an administrator reviews it before it can run.
          </div>
        </div>
      ) : (
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(258px, 1fr))", gap: 11,
        }}>
          {visible.map((skill) => (
            <GalleryCard key={skill.name} skill={skill} onOpen={setOpenSkill} onAdd={setAttachSkill} />
          ))}
        </div>
      )}

      {openSkill && (
        <SkillDetailDrawer
          skill={openSkill}
          onClose={() => setOpenSkill(null)}
          onAdd={(s) => setAttachSkill(s || openSkill)}
        />
      )}
      {attachSkill && (
        <AttachToAgentModal
          skillName={attachSkill.name}
          displayName={attachSkill.display_name || attachSkill.name}
          onClose={() => setAttachSkill(null)}
          onAttached={goAdopt}
        />
      )}
    </div>
  );
}
